import { ArrowLeft, Building2, ChevronDown, Loader2, Plus, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { cn } from "@/lib/utils";
import { tenantKeys } from "@/lib/queryKeys";
import {
  getMyTenantsSummary,
  getTenantCurrent,
} from "@/services/authService";

type TenantContextBarProps = {
  className?: string;
};

function CompanyPill({
  initial,
  imageSrc,
  imageAlt,
  name,
  withChev,
}: {
  initial: string;
  imageSrc?: string | null;
  imageAlt?: string;
  name: string;
  withChev?: boolean;
}): JSX.Element {
  return (
    <div className="fx-company-pill">
      <Avatar className="logo h-[22px] w-[22px] border-0">
        {imageSrc ? <AvatarImage src={imageSrc} alt={imageAlt ?? name} /> : null}
        <AvatarFallback className="bg-transparent text-white">
          {initial || <Building2 className="h-3 w-3" />}
        </AvatarFallback>
      </Avatar>
      <span className="name">{name}</span>
      {withChev && (
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--fx-ink-500)] transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
      )}
    </div>
  );
}

export function TenantContextBar({ className }: TenantContextBarProps): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    activeTenant,
    tenants,
    loadingTenants,
    switchTenant,
    endImpersonation: endImpersonationWithLoader,
  } = useTenantSwitcher();
  const { data: currentTenant } = useQuery({
    queryKey: tenantKeys.current(activeTenant?.id ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(activeTenant?.id),
  });

  const isAccountant =
    user?.accountType === "accountant" || user?.role === "accountant";
  // Accountants get the summary endpoint so we know per-tenant
  // onboarding state + profile image. Other users (single-tenant
  // owners, root impersonating, etc.) don't need it.
  const { data: tenantsSummary } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "tenants-summary-for-switcher"],
    queryFn: getMyTenantsSummary,
    enabled: isAccountant && Boolean(user?.id),
  });
  const summaryById = useMemo(() => {
    const map = new Map<
      string,
      { profileImage: string | null; onboardingPending: boolean }
    >();
    for (const t of tenantsSummary ?? []) {
      map.set(t.id, {
        profileImage: t.profileImage,
        onboardingPending: t.onboardingPending,
      });
    }
    return map;
  }, [tenantsSummary]);

  const headerTenantName =
    (currentTenant?.legalName?.trim() || currentTenant?.name?.trim()) ??
    activeTenant?.name ??
    "";

  const canEndImpersonation =
    user?.platformRole === "root" || user?.platformRole === "root_admin";
  const onAccountantHome = location.pathname.startsWith("/accountant");
  // Accountants on the /accountant clients list aren't "in" a tenant yet
  // (even though their JWT carries a default one), so the tenant pill +
  // back-to-clients link would be misleading there. Hide the whole bar.
  if (isAccountant && onAccountantHome) {
    return null;
  }
  const showTenantBanner = Boolean(activeTenant);
  const isNonPlatformWithTenant = showTenantBanner && !canEndImpersonation;
  const singleTenant = !loadingTenants && tenants.length === 1;
  const showTenantDropdown = isNonPlatformWithTenant && !singleTenant;

  const handleReturnToTenants = async (): Promise<void> => {
    if (canEndImpersonation) {
      await endImpersonationWithLoader();
    }
    navigate("/tenants");
  };

  const handleSelectTenant = (tenantId: string): void => {
    if (tenantId === activeTenant?.id) return;
    void switchTenant(tenantId);
  };

  if (!showTenantBanner) {
    return null;
  }

  const initial = (headerTenantName || "").trim().charAt(0).toUpperCase();

  return (
    <div className={cn("flex min-w-0 items-center gap-3", className)}>
      {singleTenant && isNonPlatformWithTenant && !isAccountant ? (
        <CompanyPill
          initial={initial}
          imageSrc={user?.tenantProfileImage}
          imageAlt={headerTenantName}
          name={headerTenantName}
        />
      ) : isNonPlatformWithTenant ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="min-w-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)] focus-visible:ring-offset-2"
              aria-label={headerTenantName || "Switch tenant"}
            >
              <CompanyPill
                initial={initial}
                imageSrc={user?.tenantProfileImage}
                imageAlt={headerTenantName}
                name={headerTenantName}
                withChev
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[14rem]">
            {loadingTenants ? (
              <div className="flex items-center justify-center gap-2 px-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("header.loadingTenants")}</span>
              </div>
            ) : (
              <>
                {tenants
                  .filter((tenant) => {
                    if (!isAccountant) return true;
                    // Always include the currently-active tenant so the
                    // user isn't stranded inside a still-onboarding
                    // tenant with no way to navigate away via the pill.
                    if (tenant.id === activeTenant?.id) return true;
                    const summary = summaryById.get(tenant.id);
                    // Before the summary fetch returns, fall back to
                    // showing all tenants — same as the old behaviour.
                    if (!summary) return true;
                    return !summary.onboardingPending;
                  })
                  .map((tenant) => {
                    const summary = summaryById.get(tenant.id);
                    const tenantName = tenant.name ?? tenant.id;
                    const tenantInitial =
                      tenantName.trim().charAt(0).toUpperCase() || "·";
                    const isActive = tenant.id === activeTenant?.id;
                    return (
                      <DropdownMenuItem
                        key={tenant.id}
                        onClick={() => handleSelectTenant(tenant.id)}
                        className="cursor-pointer gap-2"
                      >
                        <Avatar className="h-6 w-6 shrink-0 border border-[var(--fx-ink-100)]">
                          {summary?.profileImage ? (
                            <AvatarImage
                              src={summary.profileImage}
                              alt={tenantName}
                            />
                          ) : null}
                          <AvatarFallback className="bg-[var(--fx-ink-100)] text-[10px] font-semibold text-[var(--fx-ink-700)]">
                            {tenantInitial}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{tenantName}</span>
                        {isActive && (
                          <span className="ml-auto text-[var(--fx-accent-700)]">
                            ✓
                          </span>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                {isAccountant ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/accountant")}
                      className="cursor-pointer"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t(
                        "accountant.home.backToClients",
                        "Volver a clientes",
                      )}
                    </DropdownMenuItem>
                  </>
                ) : null}
                {false && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/add-company")}
                      className="cursor-pointer"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t("header.addCompany")}
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <>
          <CompanyPill
            initial={initial}
            imageSrc={user?.tenantProfileImage}
            imageAlt={headerTenantName}
            name={headerTenantName}
          />
          <button
            type="button"
            onClick={() => void handleReturnToTenants()}
            className="fx-back-link"
          >
            <X className="h-3 w-3" />
            {t("header.backToTenants")}
          </button>
        </>
      )}
    </div>
  );
}
